import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart } from 'recharts';

const DAYS_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FACTION_COLORS = ['#00D9FF', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#6366F1'];

const MetricCard = ({ label, value, sub, trendValue, loading }) => (
  <div className="card p-4 flex flex-col gap-1">
    <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
    {loading ? (
      <div className="h-7 w-24 rounded bg-white/5 animate-pulse" />
    ) : (
      <p className="text-xl font-mono font-bold text-foreground">{value}</p>
    )}
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    {trendValue !== undefined && !loading && (
      <div className={`flex items-center gap-1 text-xs font-medium ${
        trendValue > 0 ? 'text-emerald-400' : trendValue < 0 ? 'text-red-400' : 'text-muted-foreground'
      }`}>
        <span>{trendValue > 0 ? '↑' : trendValue < 0 ? '↓' : '→'}</span>
        <span>{Math.abs(trendValue)?.toFixed(1)}% vs prev 7d</span>
      </div>
    )}
  </div>
);

const SkeletonChart = ({ height = 200 }) => (
  <div className={`rounded-lg bg-white/5 animate-pulse`} style={{ height }} />
);

// Crime Heatmap Cell
const HeatmapCell = ({ count, maxCount }) => {
  const intensity = maxCount > 0 ? count / maxCount : 0;
  const bg = intensity === 0
    ? 'rgba(30,41,59,0.5)'
    : `rgba(${Math.round(30 + intensity * 209)}, ${Math.round(41 + intensity * 27)}, ${Math.round(59 + intensity * (-15))}, 0.9)`;
  return (
    <div
      className="w-full h-full rounded-sm cursor-default transition-all hover:ring-1 hover:ring-white/30"
      style={{ background: bg, minHeight: '20px' }}
      title={`${count} attempts`}
    />
  );
};

const CrimeHeatmap = ({ data, loading }) => {
  const grid = {};
  let maxCount = 0;
  (data || [])?.forEach(d => {
    const key = `${d?.day_of_week}-${d?.hour_of_day}`;
    grid[key] = d?.attempt_count;
    if (d?.attempt_count > maxCount) maxCount = d?.attempt_count;
  });

  if (loading) return <SkeletonChart height={160} />;
  if (!data || data?.length === 0) return (
    <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">No crime data for selected period</div>
  );

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[600px]">
        {/* Hour labels */}
        <div className="flex mb-1 ml-10">
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="flex-1 text-center text-xs text-muted-foreground" style={{ fontSize: '9px' }}>
              {h % 4 === 0 ? `${h}h` : ''}
            </div>
          ))}
        </div>
        {DAYS_LABELS?.map((day, dow) => (
          <div key={dow} className="flex items-center gap-1 mb-1">
            <span className="text-xs text-muted-foreground w-8 text-right flex-shrink-0" style={{ fontSize: '10px' }}>{day}</span>
            <div className="flex-1 grid gap-0.5" style={{ gridTemplateColumns: 'repeat(24, 1fr)' }}>
              {Array.from({ length: 24 }, (_, h) => (
                <HeatmapCell key={h} count={grid?.[`${dow}-${h}`] || 0} maxCount={maxCount} />
              ))}
            </div>
          </div>
        ))}
        {/* Color scale legend */}
        <div className="flex items-center gap-2 mt-2 ml-10">
          <span className="text-xs text-muted-foreground">Low</span>
          <div className="flex-1 h-2 rounded" style={{ background: 'linear-gradient(to right, rgba(30,41,59,0.5), rgba(239,68,68,0.9))' }} />
          <span className="text-xs text-muted-foreground">High</span>
        </div>
      </div>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border rounded-lg p-3 shadow-xl text-xs">
      <p className="text-muted-foreground mb-1">{label}</p>
      {payload?.map((p, i) => (
        <p key={i} style={{ color: p?.color }} className="font-mono">
          {p?.name}: {formatter ? formatter(p?.value) : p?.value}
        </p>
      ))}
    </div>
  );
};

const AnalyticsDashboard = () => {
  const [snapshots, setSnapshots] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [topEarners, setTopEarners] = useState([]);
  const [factionData, setFactionData] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [exporting, setExporting] = useState(false);
  const cacheRef = useRef({ data: null, ts: 0 });
  const refreshRef = useRef(null);

  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  const fetchAnalytics = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && cacheRef?.current?.data && (now - cacheRef?.current?.ts) < CACHE_TTL) {
      const cached = cacheRef?.current?.data;
      setSnapshots(cached?.snapshots);
      setHeatmapData(cached?.heatmap);
      setTopEarners(cached?.earners);
      setFactionData(cached?.factions);
      setMetrics(cached?.metrics);
      setLoading(false);
      setChartsLoading(false);
      return;
    }

    setChartsLoading(true);
    try {
      // Fetch 7-day snapshots
      const endDate = dateRange?.end || new Date()?.toISOString()?.split('T')?.[0];
      const startDate = dateRange?.start || new Date(Date.now() - 7 * 86400000)?.toISOString()?.split('T')?.[0];

      const [snapshotsRes, heatmapRes, earnersRes, factionsRes, statsRes] = await Promise.all([
        supabase?.from('analytics_snapshots')
          ?.select('*')
          ?.gte('snapshot_date', startDate)
          ?.lte('snapshot_date', endDate)
          ?.order('snapshot_date', { ascending: true }),
        supabase?.rpc('get_crime_heatmap', { p_days: 7 }),
        supabase?.rpc('get_top_earners', { p_days: 7 }),
        supabase?.from('factions')
          ?.select('id, faction_name, tag, member_count, is_active, total_vault_balance')
          ?.eq('is_active', true)
          ?.order('member_count', { ascending: false })
          ?.limit(8),
        supabase?.from('player_stats')?.select('user_id, net_worth, cash', { count: 'exact', head: false }),
      ]);

      const snapshotData = snapshotsRes?.data || [];
      const mapped = snapshotData?.map(s => ({
        date: new Date(s.snapshot_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        dau: s?.daily_active_users,
        tradeVolume: parseFloat(s?.total_trade_volume || 0),
        trades: s?.total_trade_count,
        crimes: s?.total_crime_attempts,
        crimeSuccess: s?.crime_success_count,
        factions: s?.active_factions,
        wars: s?.faction_wars_count,
      }));

      // Compute 7-day trends
      const computeTrend = (arr, key) => {
        if (arr?.length < 2) return 0;
        const half = Math.floor(arr?.length / 2);
        const prev = arr?.slice(0, half)?.reduce((s, d) => s + (d?.[key] || 0), 0) / half;
        const curr = arr?.slice(half)?.reduce((s, d) => s + (d?.[key] || 0), 0) / (arr?.length - half);
        return prev > 0 ? ((curr - prev) / prev) * 100 : 0;
      };

      const totalUsers = statsRes?.data?.length || 0;
      const last24hUsers = snapshotData?.[snapshotData?.length - 1]?.daily_active_users || 0;
      const lastTradeVol = snapshotData?.[snapshotData?.length - 1]?.total_trade_volume || 0;
      const totalCrimes = snapshotData?.reduce((s, d) => s + d?.total_crime_attempts, 0);
      const totalCrimeSuccess = snapshotData?.reduce((s, d) => s + d?.crime_success_count, 0);
      const crimeSuccessRate = totalCrimes > 0 ? ((totalCrimeSuccess / totalCrimes) * 100)?.toFixed(1) : 0;
      const activeWars = snapshotData?.[snapshotData?.length - 1]?.faction_wars_count || 0;

      const metricsData = {
        totalUsers,
        last24hUsers,
        lastTradeVol,
        crimeSuccessRate,
        activeWars,
        dauTrend: computeTrend(mapped, 'dau'),
        volTrend: computeTrend(mapped, 'tradeVolume'),
        crimeTrend: computeTrend(mapped, 'crimes'),
        factionTrend: computeTrend(mapped, 'factions'),
      };

      const factionPieData = (factionsRes?.data || [])?.map(f => ({
        name: f?.faction_name,
        tag: f?.tag,
        value: f?.member_count || 1,
        active: f?.is_active,
      }));

      const cache = {
        snapshots: mapped,
        heatmap: heatmapRes?.data || [],
        earners: earnersRes?.data || [],
        factions: factionPieData,
        metrics: metricsData,
      };
      cacheRef.current = { data: cache, ts: Date.now() };

      setSnapshots(mapped);
      setHeatmapData(heatmapRes?.data || []);
      setTopEarners(earnersRes?.data || []);
      setFactionData(factionPieData);
      setMetrics(metricsData);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
      setChartsLoading(false);
    }
  }, [dateRange?.start, dateRange?.end]);

  useEffect(() => {
    fetchAnalytics(true);
    refreshRef.current = setInterval(() => fetchAnalytics(true), 60000);
    return () => clearInterval(refreshRef?.current);
  }, [fetchAnalytics]);

  const handleExportCSV = useCallback(() => {
    setExporting(true);
    try {
      const headers = ['Date', 'DAU', 'Trade Volume', 'Trade Count', 'Crime Attempts', 'Crime Success', 'Active Factions', 'Wars'];
      const rows = snapshots?.map(s => [
        s?.date, s?.dau, s?.tradeVolume?.toFixed(2), s?.trades, s?.crimes, s?.crimeSuccess, s?.factions, s?.wars
      ]);
      const csv = [headers, ...rows]?.map(r => r?.join(','))?.join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      a?.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [snapshots]);

  const fmtMoney = (v) => {
    if (v >= 1000000) return `$${(v / 1000000)?.toFixed(1)}M`;
    if (v >= 1000) return `$${(v / 1000)?.toFixed(1)}K`;
    return `$${v?.toFixed(0)}`;
  };

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-heading font-bold text-foreground">Analytics Dashboard</h2>
          <p className="text-xs text-muted-foreground mt-0.5">7-day trends · Auto-refresh 60s</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <input
              type="date"
              value={dateRange?.start}
              onChange={e => setDateRange(p => ({ ...p, start: e?.target?.value }))}
              className="bg-surface border border-border rounded px-2 py-1 text-xs text-foreground focus:outline-none focus:border-primary/50"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <input
              type="date"
              value={dateRange?.end}
              onChange={e => setDateRange(p => ({ ...p, end: e?.target?.value }))}
              className="bg-surface border border-border rounded px-2 py-1 text-xs text-foreground focus:outline-none focus:border-primary/50"
            />
            <button
              onClick={() => fetchAnalytics(true)}
              className="px-3 py-1 bg-primary/10 text-primary border border-primary/30 rounded text-xs hover:bg-primary/20 transition-colors"
            >
              Apply
            </button>
          </div>
          <button
            onClick={handleExportCSV}
            disabled={exporting || snapshots?.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-surface border border-border rounded text-xs text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors disabled:opacity-50"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
            </svg>
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>
      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <MetricCard
          label="Total Users"
          value={metrics?.totalUsers?.toLocaleString() || '—'}
          sub="All-time"
          loading={loading}
        />
        <MetricCard
          label="Active Users"
          value={metrics?.last24hUsers?.toLocaleString() || '—'}
          sub="Last 24h"
          trendValue={metrics?.dauTrend}
          loading={loading}
        />
        <MetricCard
          label="Trade Volume"
          value={metrics ? fmtMoney(metrics?.lastTradeVol) : '—'}
          sub="Last 24h"
          trendValue={metrics?.volTrend}
          loading={loading}
        />
        <MetricCard
          label="Crime Success"
          value={metrics ? `${metrics?.crimeSuccessRate}%` : '—'}
          sub="Last 7 days"
          trendValue={metrics?.crimeTrend}
          loading={loading}
        />
        <MetricCard
          label="Active Wars"
          value={metrics?.activeWars?.toLocaleString() || '0'}
          sub="Current"
          loading={loading}
        />
      </div>
      {/* DAU Line Chart */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Daily Active Users (7-day)</h3>
        {chartsLoading ? <SkeletonChart height={200} /> : snapshots?.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No data for selected period</div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={snapshots}>
              <defs>
                <linearGradient id="dauGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00D9FF" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00D9FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="dau" stroke="#00D9FF" strokeWidth={2} fill="url(#dauGrad)" name="DAU" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      {/* Trade Volume Bar Chart */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Daily Trade Volume</h3>
        {chartsLoading ? <SkeletonChart height={200} /> : snapshots?.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No data for selected period</div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={snapshots}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} tickFormatter={fmtMoney} />
              <Tooltip content={<CustomTooltip formatter={fmtMoney} />} />
              <Bar dataKey="tradeVolume" fill="#10B981" radius={[3, 3, 0, 0]} name="Volume" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      {/* Crime Heatmap + Faction Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Crime Activity Heatmap (7d)</h3>
          <CrimeHeatmap data={heatmapData} loading={chartsLoading} />
        </div>

        <div className="card p-5">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Faction Participation</h3>
          {chartsLoading ? <SkeletonChart height={200} /> : factionData?.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No faction data</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={factionData}
                  cx="50%" cy="50%"
                  innerRadius={50} outerRadius={80}
                  dataKey="value"
                  nameKey="name"
                  label={({ tag, percent }) => `${tag} ${(percent * 100)?.toFixed(0)}%`}
                  labelLine={false}
                >
                  {factionData?.map((_, i) => (
                    <Cell key={i} fill={FACTION_COLORS?.[i % FACTION_COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '12px' }}
                  formatter={(v, n) => [v + ' members', n]}
                />
                <Legend
                  formatter={(value) => <span style={{ fontSize: '11px', color: 'var(--color-muted-foreground)' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      {/* Top Earners Table */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Top Earners (7-day Net Worth Change)</h3>
        {chartsLoading ? (
          <div className="space-y-2">
            {Array(5)?.fill(0)?.map((_, i) => (
              <div key={i} className="h-10 rounded bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : topEarners?.length === 0 ? (
          <div className="flex items-center justify-center h-24 text-muted-foreground text-sm">No earner data available</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  {['Rank', 'Username', 'Starting NW', 'Current NW', 'Change', '%']?.map(h => (
                    <th key={h} className="text-left py-2 px-2 text-xs text-muted-foreground font-medium uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topEarners?.map((e, i) => {
                  const change = parseFloat(e?.change_amount || 0);
                  const pct = parseFloat(e?.change_pct || 0);
                  const isPos = change >= 0;
                  return (
                    <tr key={e?.user_id || i} className="border-b border-border/30 hover:bg-white/5 transition-colors">
                      <td className="py-2 px-2">
                        <span className={`font-mono font-bold text-xs ${
                          i === 0 ? 'text-yellow-400' : i === 1 ? 'text-zinc-300' : i === 2 ? 'text-amber-600' : 'text-muted-foreground'
                        }`}>#{i + 1}</span>
                      </td>
                      <td className="py-2 px-2 font-medium text-foreground">{e?.username || 'Unknown'}</td>
                      <td className="py-2 px-2 font-mono text-xs text-muted-foreground">{fmtMoney(parseFloat(e?.starting_net_worth || 0))}</td>
                      <td className="py-2 px-2 font-mono text-xs text-foreground">{fmtMoney(parseFloat(e?.current_net_worth || 0))}</td>
                      <td className={`py-2 px-2 font-mono text-xs font-semibold ${isPos ? 'text-emerald-400' : 'text-red-400'}`}>
                        {isPos ? '+' : ''}{fmtMoney(change)}
                      </td>
                      <td className={`py-2 px-2 font-mono text-xs font-semibold ${isPos ? 'text-emerald-400' : 'text-red-400'}`}>
                        {isPos ? '+' : ''}{pct?.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
